import { PLAN_LIMITS } from '@/lib/plan';
import { pageSlice } from '@/lib/paginate';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'https://api.recost.dev';
const DEV = import.meta.env.VITE_DEV_AUTH === 'true';

// ─── Access token singleton (in-memory only) ─────────────────────────────────
// Replaces localStorage-based persistence. See
// docs/superpowers/specs/2026-05-23-token-storage-design.md §2 for rationale.

let accessToken: string | null = null;
const subscribers = new Set<(t: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  subscribers.forEach((cb) => cb(token));
}

export function onAccessTokenChange(cb: (t: string | null) => void): () => void {
  subscribers.add(cb);
  return () => { subscribers.delete(cb); };
}

// ─── Single-flight refresh helper ────────────────────────────────────────────
// All concurrent 401s coalesce into one in-flight POST /auth/refresh.

let refreshInFlight: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = await res.json() as { data: { token: string } };
      setAccessToken(body.data.token);
      return body.data.token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

// ─── Structured API error (#19) ──────────────────────────────────────────────
// The API envelopes every error as { error: { code, message, details } }.
// ApiError carries those fields so callers can branch on .code without parsing
// the message string. .message stays human-readable for direct display.
export class ApiError extends Error {
  code?: string;
  status: number;
  details?: unknown;
  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Pure normalizer: turn a parsed error body + status into an ApiError.
// Reads err.error.message first, then a legacy top-level err.message, then a
// generic fallback. Exported for unit testing.
export function buildApiError(raw: unknown, status: number): ApiError {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const envelope = obj.error as { code?: string; message?: string; details?: unknown } | undefined;
  const message =
    envelope?.message ??
    (obj as { message?: string }).message ??
    `Request failed: ${status}`;
  return new ApiError(message, status, envelope?.code, envelope?.details);
}

// ─── Core request: credentials: 'include' + 401 → refresh → retry ────────────

async function request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
  const token = accessToken;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 401 && !isRetry) {
    const newToken = await refresh();
    if (newToken) {
      return request<T>(path, options, true);
    }
    setAccessToken(null);
    throw new SessionExpiredError();
  }

  if (!res.ok) {
    const raw = await res.json().catch(() => ({}));
    throw buildApiError(raw, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as T;
}

// ─── Safe API path builder ───────────────────────────────────────────────────
// Encodes interpolated *path segments* so a crafted id (e.g. from useParams)
// can't escape the namespace (#38). Use for the path portion only; append
// query strings raw (their values are app-controlled). Separators in the
// literal portions are preserved.
export function apiPath(strings: TemplateStringsArray, ...values: (string | number)[]): string {
  return strings.reduce(
    (acc, s, i) => acc + s + (i < values.length ? encodeURIComponent(String(values[i])) : ''),
    '',
  );
}

// Mutable dev-mode state — flipped by POST /auth/onboarding
let mockOnboardingCompleted = false;

// ---------------------------------------------------------------------------
// Mock handler — only active when VITE_DEV_AUTH=true
// ---------------------------------------------------------------------------

// Cache the dynamic import so the module resolves once, not per request (#40).
// Kept dynamic so mock-data stays out of the production bundle.
let mockDataPromise: Promise<typeof import('@/lib/mock-data')> | null = null;
const loadMockData = () => (mockDataPromise ??= import('@/lib/mock-data'));

async function mockRequest<T>(path: string, method = 'GET', _body?: unknown): Promise<T> {
  const {
    MOCK_USER, MOCK_PROJECTS, MOCK_KEYS,
    MOCK_ENDPOINTS_MAP, MOCK_SUGGESTIONS_MAP, MOCK_COST_BY_PROVIDER_MAP,
    MOCK_COST_BY_FILE_MAP, MOCK_SCANS_MAP, MOCK_LATEST_SCAN_MAP, MOCK_COST_SUMMARY_MAP,
    MOCK_PARSER_RUNS, MOCK_PARSER_RESULTS_MAP,
  } = await loadMockData();

  await new Promise((r) => setTimeout(r, 100));

  const paginated = <D>(data: D[]) => ({
    data,
    pagination: { page: 1, limit: 50, total: data.length, totalPages: 1, hasNext: false, hasPrev: false },
  });

  // Auth / onboarding endpoints
  if (path === '/auth/me') {
    return { data: { ...MOCK_USER, onboarding_completed: mockOnboardingCompleted } } as T;
  }
  if (path === '/auth/onboarding' && method === 'POST') {
    mockOnboardingCompleted = true;
    return { ok: true } as T;
  }
  if (path === '/auth/onboarding') {
    return { data: { occupation: null, building_type: null, use_case: null, referral_source: null, completed: mockOnboardingCompleted } } as T;
  }

  if (path === '/auth/usage') {
    const resetsAt = new Date(Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate() + 1,
    )).toISOString();
    return {
      data: {
        projects:   { used: MOCK_PROJECTS.length,  limit: PLAN_LIMITS.projects },
        apiKeys:    { used: 1,                       limit: PLAN_LIMITS.apiKeys },
        scansToday: { used: 12, limit: PLAN_LIMITS.scansToday, resetsAt },
        endpoints:  { used: 12, limit: PLAN_LIMITS.endpoints, projectName: MOCK_PROJECTS[0].name },
      },
    } as T;
  }

  if (path === '/auth/keys' && method === 'GET') return { data: MOCK_KEYS } as T;
  if (path === '/auth/keys' && method === 'POST') {
    const name = typeof (_body as { name?: unknown })?.name === 'string' ? (_body as { name: string }).name : 'mock-key';
    const created = {
      id: `key_mock_${Date.now()}`,
      key: 'rct_test_0000000000000000000000000000',
      name,
      key_prefix: 'rct_test_***0000',
      created_at: new Date().toISOString(),
    };
    return { data: created } as T;
  }
  const keyDeleteMatch = path.match(/^\/auth\/keys\/[^/]+$/);
  if (keyDeleteMatch && method === 'DELETE') return undefined as T;
  if (path === '/projects' || path.startsWith('/projects?')) return paginated(MOCK_PROJECTS) as T;

  const projectMatch = path.match(/^\/projects\/([^/]+)$/);
  if (projectMatch) {
    const proj = MOCK_PROJECTS.find((p) => p.id === projectMatch[1]) ?? MOCK_PROJECTS[0];
    return { data: proj } as T;
  }

  const subMatch = path.match(/^\/projects\/([^/]+)\/(.+)$/);
  if (subMatch) {
    const pid = subMatch[1];
    const sub = subMatch[2];

    const endpoints = MOCK_ENDPOINTS_MAP[pid] ?? MOCK_ENDPOINTS_MAP[MOCK_PROJECTS[0].id];
    const suggestions = MOCK_SUGGESTIONS_MAP[pid] ?? MOCK_SUGGESTIONS_MAP[MOCK_PROJECTS[0].id];
    const scans = MOCK_SCANS_MAP[pid] ?? MOCK_SCANS_MAP[MOCK_PROJECTS[0].id];
    const latestScan = MOCK_LATEST_SCAN_MAP[pid] ?? MOCK_LATEST_SCAN_MAP[MOCK_PROJECTS[0].id];
    const costByProvider = MOCK_COST_BY_PROVIDER_MAP[pid] ?? MOCK_COST_BY_PROVIDER_MAP[MOCK_PROJECTS[0].id];
    const costByFile = MOCK_COST_BY_FILE_MAP[pid] ?? MOCK_COST_BY_FILE_MAP[MOCK_PROJECTS[0].id];
    const costSummary = MOCK_COST_SUMMARY_MAP[pid] ?? MOCK_COST_SUMMARY_MAP[MOCK_PROJECTS[0].id];

    if (sub === 'scans/latest') return { data: latestScan } as T;
    if (sub.match(/^scans\/[^/]+$/) && method === 'DELETE') return undefined as T;
    if (sub.match(/^scans(\?|$)/)) return paginated(scans) as T;
    if (sub.match(/^endpoints/)) {
      const qp = new URLSearchParams(sub.split('?')[1] ?? '');
      const scanId = qp.get('scanId');
      const page = Number(qp.get('page') ?? '1');
      const limit = Number(qp.get('limit') ?? '100');
      if (scanId && !scans.some((s) => s.id === scanId)) {
        return pageSlice<typeof endpoints[number]>([], page, limit) as T;
      }
      return pageSlice(endpoints, page, limit) as T;
    }
    if (sub.match(/^suggestions/)) {
      const qp = new URLSearchParams(sub.split('?')[1] ?? '');
      const scanId = qp.get('scanId');
      if (scanId && !scans.some((s) => s.id === scanId)) {
        return paginated<typeof suggestions[number]>([]) as T;
      }
      return paginated(suggestions) as T;
    }
    if (sub.match(/^cost\/by-provider/)) return paginated(costByProvider) as T;
    if (sub.match(/^cost\/by-file/)) return paginated(costByFile) as T;
    if (sub === 'cost') return { data: costSummary } as T;

    if (sub.match(/^analytics/)) {
      const FACTORS = [0.72,0.88,0.95,1.10,0.84,1.20,0.78,0.91,1.05,0.93,1.15,0.82,0.99,1.08,0.75,1.18,0.86,1.00,0.94,1.12,0.79,1.22,0.88,0.97,1.06,0.83,0.91,1.14,0.76,1.19];
      const HOUR_FACTORS = [0.55,0.42,0.38,0.31,0.28,0.33,0.51,0.74,0.92,1.08,1.15,1.18,1.12,1.10,1.14,1.20,1.18,1.09,1.03,0.98,0.91,0.82,0.72,0.61];
      const qp = new URLSearchParams(sub.split('?')[1] ?? '');
      const interval = qp.get('interval') ?? 'day';
      const daily = costSummary.totalMonthlyCost / 30;

      let data: { date?: string; windowStart?: string; totalCostCents: number; requestCount: number }[];
      if (interval === 'window') {
        // 24h — one point per hour
        data = HOUR_FACTORS.map((f, i) => ({
          windowStart: new Date(Date.now() - (23 - i) * 3_600_000).toISOString(),
          totalCostCents: Math.round(Math.max(0, (daily / 24) * f) * 100),
          requestCount: Math.round((costSummary.totalCallsPerDay / 24) * f),
        }));
      } else {
        // day interval — use from/to to determine how many days
        const from = qp.get('from') ? new Date(qp.get('from')!).getTime() : Date.now() - 30 * 86_400_000;
        const to   = qp.get('to')   ? new Date(qp.get('to')!).getTime()   : Date.now();
        const days = Math.max(1, Math.round((to - from) / 86_400_000));
        const factors = days <= 7
          ? FACTORS.slice(-7)
          : FACTORS.slice(-days);
        data = factors.map((f, i) => ({
          date: new Date(from + i * 86_400_000).toISOString(),
          totalCostCents: Math.round(Math.max(0, daily * f) * 100),
          requestCount: Math.round(costSummary.totalCallsPerDay * f),
        }));
      }
      return { data: { data } } as T;
    }

    if (sub === 'sustainability') {
      return { data: { estimatedCO2Grams: 142.8, energyKWh: 0.34, equivalentDrivingKm: 0.9, treeDaysOffset: 0.5 } } as T;
    }

    if (sub.match(/^telemetry\/recent/)) {
      const now = Date.now();
      const data = [
        { id: 'w1',  timestamp: new Date(now - 90_000).toISOString(),   endpoint: '/v1/chat/completions', provider: 'OpenAI',    model: 'gpt-4o',                  tokensIn: 1842, tokensOut: 312, cost: 0.0421, latencyMs: 1240, environment: 'production' },
        { id: 'w2',  timestamp: new Date(now - 210_000).toISOString(),  endpoint: '/v1/messages',          provider: 'Anthropic', model: 'claude-sonnet-4-6',       tokensIn: 2104, tokensOut: 489, cost: 0.0318, latencyMs: 980,  environment: 'production' },
        { id: 'w3',  timestamp: new Date(now - 350_000).toISOString(),  endpoint: '/v1/embeddings',        provider: 'OpenAI',    model: 'text-embedding-3-small',  tokensIn: 512,  tokensOut: 0,   cost: 0.0001, latencyMs: 142,  environment: 'production' },
        { id: 'w4',  timestamp: new Date(now - 510_000).toISOString(),  endpoint: '/v1/chat/completions', provider: 'OpenAI',    model: 'gpt-4o-mini',             tokensIn: 934,  tokensOut: 201, cost: 0.0031, latencyMs: 620,  environment: 'staging'    },
        { id: 'w5',  timestamp: new Date(now - 700_000).toISOString(),  endpoint: '/v1/messages',          provider: 'Anthropic', model: 'claude-3-haiku',          tokensIn: 788,  tokensOut: 156, cost: 0.0008, latencyMs: 310,  environment: 'production' },
        { id: 'w6',  timestamp: new Date(now - 870_000).toISOString(),  endpoint: '/v1/chat/completions', provider: 'OpenAI',    model: 'gpt-4o',                  tokensIn: 3201, tokensOut: 622, cost: 0.0712, latencyMs: 2180, environment: 'production' },
        { id: 'w7',  timestamp: new Date(now - 1020_000).toISOString(), endpoint: '/v1/embeddings',        provider: 'OpenAI',    model: 'text-embedding-3-small',  tokensIn: 1024, tokensOut: 0,   cost: 0.0002, latencyMs: 158,  environment: 'development'},
        { id: 'w8',  timestamp: new Date(now - 1190_000).toISOString(), endpoint: '/v1/messages',          provider: 'Anthropic', model: 'claude-sonnet-4-6',       tokensIn: 1654, tokensOut: 338, cost: 0.0248, latencyMs: 870,  environment: 'production' },
        { id: 'w9',  timestamp: new Date(now - 1340_000).toISOString(), endpoint: '/v1/chat/completions', provider: 'OpenAI',    model: 'gpt-4o-mini',             tokensIn: 612,  tokensOut: 144, cost: 0.0019, latencyMs: 410,  environment: 'staging'    },
        { id: 'w10', timestamp: new Date(now - 1540_000).toISOString(), endpoint: '/v1/messages',          provider: 'Anthropic', model: 'claude-opus-4-6',         tokensIn: 2890, tokensOut: 744, cost: 0.1182, latencyMs: 3420, environment: 'production' },
      ];
      return { data } as T;
    }
  }

  // Parser runs
  if (path.match(/^\/parser\/runs(\?|$)/)) return { data: MOCK_PARSER_RUNS } as T;
  const parserResultsMatch = path.match(/^\/parser\/runs\/([^/]+)\/results$/);
  if (parserResultsMatch) {
    const rid = parserResultsMatch[1];
    return { data: MOCK_PARSER_RESULTS_MAP[rid] ?? [] } as T;
  }
  if (path === '/parser/runs') return { data: { runId: 'run_mock_new', status: 'queued', createdAt: Date.now() } } as T;

  console.warn(`[mock] unhandled ${method} ${path}`);
  return { data: null } as T;
}

export const apiClient = {
  get: <T>(path: string) => DEV ? mockRequest<T>(path) : request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    DEV ? mockRequest<T>(path, 'POST', body) : request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    DEV ? mockRequest<T>(path, 'PATCH', body) : request<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: (path: string) =>
    DEV ? mockRequest<void>(path, 'DELETE') : request<void>(path, { method: 'DELETE' }),
};

export type BugSeverity = 'blocker' | 'major' | 'minor' | 'nitpick';
export type BugArea = 'sdk' | 'vscode' | 'mcp' | 'dashboard' | 'landing' | 'docs' | 'other';

export interface BugReportPayload {
  title: string;
  description: string;
  severity: BugSeverity;
  area: BugArea;
  email?: string;
  website?: string;
  url?: string;
  referrer?: string;
  app_version?: string;
  user_agent?: string;
}

export async function submitBugReport(payload: BugReportPayload): Promise<unknown> {
  // /bug-reports is an anonymous endpoint — never attach the access token (#35).
  const res = await fetch(`${API_BASE_URL}/bug-reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Reuse the shared normalizer (#20) — but keep the dedicated fetch above:
    // /bug-reports is anonymous and must never receive the access token (#35).
    const raw = await res.json().catch(() => ({}));
    throw buildApiError(raw, res.status);
  }
  if (res.status === 204) return undefined;
  return res.json();
}
